import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2, UserCog } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../api";
import {
  Button,
  ConfirmDialog,
  EmptyState,
  FormField,
  Modal,
  Pagination,
  Panel,
  SearchBox,
} from "../components/ui";
import { PageHeader } from "../components/shell";
import type { PageMeta } from "../types";
import { formatDate } from "./catalogs";

type HistoryItem = {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  actor_email?: string;
  created_at: string;
  metadata: Record<string, unknown>;
};
type UserItem = {
  id: string;
  email: string;
  display_name: string;
  is_active: number;
  created_at: string;
  roles: string | null;
  roleIds?: string[];
};
type CustomField = {
  id: string;
  applies_to: "bom_header" | "bom_line";
  field_key: string;
  label: string;
  field_type: "text" | "number" | "date" | "boolean" | "select";
  selectOptions: string[];
  is_required: number;
  is_active: number;
  sort_order: number;
};
type Paged<T> = { items: T[]; meta: PageMeta };
type RoleDirectory = {
  roles: Array<{
    id: string;
    name: string;
    description: string;
    isActive: boolean;
    isSystem: boolean;
    userCount: number;
    activeUserCount: number;
    permissions: Array<{ id: string; code: string; description: string }>;
  }>;
  permissions: Array<{ id: string; code: string; description: string }>;
};

const roles = [
  { id: "superadmin", label: "Quản trị hệ thống" },
  { id: "bom_editor", label: "Biên soạn BOM" },
  { id: "bom_approver", label: "Phê duyệt BOM" },
  { id: "viewer", label: "Chỉ xem" },
] as const;

const roleLabels: Record<string, string> = {
  superadmin: "Quản trị hệ thống",
  bom_editor: "Biên soạn BOM",
  bom_approver: "Phê duyệt BOM",
  viewer: "Chỉ xem",
  Superadmin: "Quản trị hệ thống",
  "Biên soạn BOM": "Biên soạn BOM",
  "Phê duyệt BOM": "Phê duyệt BOM",
  "Chỉ xem": "Chỉ xem",
};

function roleIdsFromList(value: string | null): string[] {
  const roleIdByName: Record<string, string> = {
    superadmin: "superadmin",
    "biên soạn bom": "bom_editor",
    "phê duyệt bom": "bom_approver",
    "chỉ xem": "viewer",
    bom_editor: "bom_editor",
    bom_approver: "bom_approver",
    viewer: "viewer",
  };
  return (value ?? "")
    .split(",")
    .map((role) => roleIdByName[role.trim().toLocaleLowerCase("vi")])
    .filter((role): role is string => Boolean(role));
}

function roleLabel(role: string): string {
  return roleLabels[role] ?? "Người dùng";
}

function roleDisplayName(role: { id: string; name: string }): string {
  return role.id === "superadmin" ? "Quản trị hệ thống" : role.name;
}

const activityLabels: Record<string, string> = {
  "admin.role_create": "Thêm vai trò",
  "admin.role_update": "Cập nhật vai trò",
  "admin.role_disable": "Ngừng sử dụng vai trò",
  "system.bootstrap": "Khởi tạo hệ thống",
  "auth.sso_login": "Đăng nhập Microsoft",
  "catalog.create": "Thêm danh mục",
  "catalog.update": "Cập nhật danh mục",
  "catalog.delete": "Xóa danh mục",
  "catalog.inventory_import_create": "Bổ sung danh mục từ tệp tồn kho",
  "bom.create_draft": "Tạo BOM nháp",
  "bom.line_add": "Thêm vật tư vào BOM",
  "bom.line_update": "Cập nhật vật tư trong BOM",
  "bom.line_delete": "Xóa vật tư khỏi BOM",
  "bom.delete": "Xóa BOM",
  "bom.submit_review": "Gửi BOM phê duyệt",
  "bom.approve": "Phê duyệt BOM",
  "calculation.run": "Tính nhu cầu vật tư",
  "calculation.norm_adjust": "Điều chỉnh định mức theo phiếu",
  "calculation.delete": "Xóa phiếu tính",
  "inventory.confirm_use": "Chốt phiếu và trừ tồn kho",
  "custom_field.create": "Thêm trường thông tin",
  "custom_field.update": "Cập nhật trường thông tin",
  "custom_field.delete": "Xóa trường thông tin",
  "admin.user_create": "Thêm người dùng",
  "admin.user_update": "Cập nhật người dùng",
};
const fieldTypeLabels: Record<CustomField["field_type"], string> = { text: "Văn bản", number: "Số", date: "Ngày", boolean: "Có / Không", select: "Danh sách chọn" };
function activityLabel(action: string) { return activityLabels[action] ?? "Cập nhật dữ liệu"; }
function activityDetail(metadata: Record<string, unknown>) {
  if (typeof metadata.code !== "string") return "—";
  return metadata.code.startsWith("CALC-") ? `Phiếu tính ${metadata.code}` : `BOM ${metadata.code}`;
}

export function HistoryPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const history = useQuery({ queryKey: ["history", page], queryFn: () => api<Paged<HistoryItem>>(`/history?page=${page}&pageSize=25`) });
  useEffect(() => setPage(1), [search]);
  const activityRows = history.data?.items.filter((item) => JSON.stringify(item).toLocaleLowerCase("vi").includes(search.toLocaleLowerCase("vi"))) ?? [];
  return <div className="page voucher-history-page">
    <PageHeader title="Lịch sử hoạt động" />
    <Panel title="Hoạt động hệ thống">
      <div className="table-toolbar"><SearchBox value={search} onChange={setSearch} placeholder="Lọc trong lịch sử hoạt động..." /></div>
      {history.isLoading ? <div className="loading-space">Đang tải lịch sử…</div> : !activityRows.length ? <EmptyState title="Chưa có lịch sử hoạt động" detail="Nhật ký tạo, điều chỉnh, chốt phiếu, BOM và danh mục sẽ xuất hiện tại đây." /> : <><div className="table-scroll"><table><thead><tr><th>Thời điểm</th><th>Người thực hiện</th><th>Hoạt động</th><th>Đối tượng</th><th>Chi tiết</th></tr></thead><tbody>{activityRows.map((row) => <tr key={row.id}><td>{formatDate(row.created_at)}</td><td>{row.actor_email ?? "Hệ thống"}</td><td>{activityLabel(row.action)}</td><td>{activityDetail(row.metadata)}</td><td>—</td></tr>)}</tbody></table></div><Pagination meta={history.data?.meta} onPage={setPage} /></>}
    </Panel>
  </div>;
}

export function AdminPage() {
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<"users" | "fields" | "roles">("users");
  const [userModal, setUserModal] = useState<UserItem | "new" | null>(null);
  const [fieldModal, setFieldModal] = useState<CustomField | "new" | null>(
    null,
  );
  const [roleModal, setRoleModal] = useState<RoleDirectory["roles"][number] | "new" | null>(null);
  const users = useQuery({
    queryKey: ["admin-users", page],
    queryFn: () =>
      api<Paged<UserItem>>(`/admin/users?page=${page}&pageSize=25`),
  });
  const fields = useQuery({
    queryKey: ["custom-fields"],
    queryFn: () => api<{ items: CustomField[] }>("/custom-fields"),
  });
  const roleDirectory = useQuery({
    queryKey: ["admin-roles"],
    queryFn: () => api<RoleDirectory>("/admin/roles"),
    enabled: true,
  });
  return (
    <div className="page">
      <PageHeader
        title="Quản trị hệ thống"
        actions={
          tab === "users" ? (
            <Button onClick={() => setUserModal("new")}>
              <Plus size={17} /> Thêm người dùng
            </Button>
          ) : tab === "fields" ? (
            <Button onClick={() => setFieldModal("new")}>
              <Plus size={17} /> Thêm trường
            </Button>
          ) : null
        }
      />
      <Panel>
        <div className="table-toolbar">
          <div className="catalog-tabs" role="tablist" aria-label="Quản trị hệ thống">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "users"}
              className={tab === "users" ? "selected" : ""}
              onClick={() => setTab("users")}
            >
              Người dùng & phân quyền
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "fields"}
              className={tab === "fields" ? "selected" : ""}
              onClick={() => setTab("fields")}
            >
              Trường tùy biến BOM
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "roles"}
              className={tab === "roles" ? "selected" : ""}
              onClick={() => setTab("roles")}
            >
              Vai trò & quyền hạn
            </button>
          </div>
        </div>
        {tab === "users" ? (
          users.isLoading ? (
            <div className="loading-space">Đang tải người dùng…</div>
          ) : !users.data?.items.length ? (
            <EmptyState
              title="Chưa có người dùng"
              detail="Tài khoản quản trị được tạo sẵn khi hệ thống đi vào hoạt động."
            />
          ) : (
            <>
              <div className="table-scroll">
                <table className="admin-users-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Tên hiển thị</th>
                      <th>Vai trò</th>
                      <th>Trạng thái</th>
                      <th>Tạo lúc</th>
                      <th aria-label="Thao tác" />
                    </tr>
                  </thead>
                  <tbody>
                    {users.data.items.map((item) => (
                      <tr key={item.id}>
                        <td className="code-cell">{item.email}</td>
                        <td>{item.display_name}</td>
                        <td>
                          {(item.roles ?? "")
                            .split(",")
                            .filter(Boolean)
                            .map((role) => role.trim())
                            .join(", ") || "—"}
                        </td>
                        <td>
                          <span
                            className={
                              item.is_active
                                ? "status active"
                                : "status superseded"
                            }
                          >
                            {item.is_active ? "Đang dùng" : "Đã khóa"}
                          </span>
                        </td>
                        <td>{formatDate(item.created_at)}</td>
                        <td className="row-actions">
                          <button
                            type="button"
                            title="Cập nhật người dùng"
                            aria-label={`Cập nhật ${item.email}`}
                            onClick={() => setUserModal(item)}
                          >
                            <Pencil size={16} />
                          </button>
                          <DeleteUser item={item} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination meta={users.data.meta} onPage={setPage} />
            </>
          )
        ) : tab === "fields" ? !fields.data?.items.length ? (
          <EmptyState
            title="Chưa có trường tùy biến"
            detail="Tạo trường linh hoạt cho phần đầu BOM hoặc từng dòng vật tư."
          />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Nhãn</th>
                  <th>Mã nhận diện</th>
                  <th>Áp dụng</th>
                  <th>Kiểu</th>
                  <th>Bắt buộc</th>
                  <th>Trạng thái</th>
                  <th aria-label="Thao tác" />
                </tr>
              </thead>
              <tbody>
                {fields.data.items.map((field) => (
                  <tr key={field.id}>
                    <td>{field.label}</td>
                    <td>{field.field_key}</td>
                    <td>
                      {field.applies_to === "bom_header"
                        ? "Thông tin chung của định mức"
                        : "Chi tiết từng vật tư"}
                    </td>
                    <td>{fieldTypeLabels[field.field_type]}</td>
                    <td>{field.is_required ? "Có" : "Không"}</td>
                    <td>
                      <span
                        className={
                          field.is_active
                            ? "status active"
                            : "status superseded"
                        }
                      >
                        {field.is_active ? "Đang dùng" : "Ngừng dùng"}
                      </span>
                    </td>
                    <td className="row-actions">
                      <button
                        type="button"
                        title="Cập nhật trường"
                        aria-label={`Cập nhật ${field.label}`}
                        onClick={() => setFieldModal(field)}
                      >
                        <Pencil size={16} />
                      </button>
                      <DeleteCustomField id={field.id} label={field.label} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === "roles" ? (
          <div className="role-management">
            <div className="role-management-heading">
              <div>
                <h3>Vai trò và quyền thao tác</h3>
                <p>Thiết lập quyền thao tác cho từng nhóm người dùng. Vai trò Quản trị hệ thống luôn được bảo vệ.</p>
              </div>
              <Button onClick={() => setRoleModal("new")}>
                <Plus size={17} /> Thêm vai trò
              </Button>
            </div>
            {roleDirectory.isLoading ? (
              <div className="loading-space">Đang tải vai trò và quyền thao tác…</div>
            ) : roleDirectory.isError ? (
              <div className="empty-state"><h3>Chưa thể tải vai trò</h3><p>Vui lòng thử lại sau ít phút.</p></div>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Vai trò</th>
                      <th>Quyền thao tác</th>
                      <th>Người dùng</th>
                      <th>Trạng thái</th>
                      <th aria-label="Thao tác" />
                    </tr>
                  </thead>
                  <tbody>
                    {roleDirectory.data?.roles.map((role) => (
                      <tr key={role.id}>
                        <td>
                          <strong>{roleDisplayName(role)}</strong>
                          <small className="table-note">{role.description}</small>
                        </td>
                        <td>
                          <span className="permission-count">{role.permissions.length} quyền thao tác</span>
                          <small className="table-note">{role.permissions.map((permission) => permission.description).join(", ")}</small>
                        </td>
                        <td>{role.activeUserCount} đang dùng / {role.userCount} được gán</td>
                        <td>
                          <span className={role.isActive ? "status active" : "status superseded"}>
                            {role.isActive ? "Đang dùng" : "Ngừng dùng"}
                          </span>
                        </td>
                        <td className="row-actions">
                          {role.isSystem ? (
                            <span className="protected-role">Được bảo vệ</span>
                          ) : (
                            <button type="button" title="Cập nhật vai trò" aria-label={`Cập nhật ${roleDisplayName(role)}`} onClick={() => setRoleModal(role)}>
                              <Pencil size={16} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : roleDirectory.isLoading ? (
          <div className="loading-space">Đang tải vai trò và quyền hạn…</div>
        ) : (
          <div className="role-directory">
            <section>
              <h3>Vai trò</h3>
              {roleDirectory.data?.roles.map((role) => (
                <div className="role-card" key={role.id}>
                  <strong>{roleLabel(role.id)}</strong>
                  <p>{role.description}</p>
                  <ul>
                    {role.permissions.map((permission) => <li key={permission.id}>{permission.description}</li>)}
                  </ul>
                </div>
              ))}
            </section>
            <section>
              <h3>Danh mục quyền hạn</h3>
              <div className="table-scroll">
                <table>
                  <thead><tr><th>Quyền hạn</th><th>Ý nghĩa</th></tr></thead>
                  <tbody>{roleDirectory.data?.permissions.map((permission) => <tr key={permission.id}><td>{permission.code}</td><td>{permission.description}</td></tr>)}</tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </Panel>
      <UserModal item={userModal} roles={roleDirectory.data?.roles ?? []} onClose={() => setUserModal(null)} />
      <CustomFieldModal item={fieldModal} onClose={() => setFieldModal(null)} />
      <RoleModal item={roleModal} permissions={roleDirectory.data?.permissions ?? []} onClose={() => setRoleModal(null)} />
    </div>
  );
}

function UserModal({
  item,
  roles: availableRoles,
  onClose,
}: {
  item: UserItem | "new" | null;
  roles: RoleDirectory["roles"];
  onClose(): void;
}) {
  const client = useQueryClient();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [roleIds, setRoleIds] = useState<string[]>(["viewer"]);
  const [isActive, setIsActive] = useState(true);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const editing = item && item !== "new" ? item : null;

  useEffect(() => {
    if (editing) {
      setEmail(editing.email);
      setDisplayName(editing.display_name);
      setRoleIds(editing.roleIds?.length ? editing.roleIds : roleIdsFromList(editing.roles));
      setIsActive(Boolean(editing.is_active));
    } else {
      setEmail("");
      setDisplayName("");
      setRoleIds(availableRoles.find((role) => role.id === "viewer" && role.isActive)?.id ? ["viewer"] : []);
      setIsActive(true);
    }
    setPassword("");
    setError("");
  }, [editing, item, availableRoles]);

  function toggleRole(roleId: string) {
    setRoleIds((current) =>
      current.includes(roleId)
        ? current.filter((id) => id !== roleId)
        : [...current, roleId],
    );
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!item) return;
    if (!roleIds.length) {
      setError("Hãy chọn ít nhất một vai trò cho người dùng.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api(item === "new" ? "/admin/users" : `/admin/users/${editing?.id}`, {
        method: item === "new" ? "POST" : "PUT",
        body: JSON.stringify({
          email,
          displayName,
          roleIds,
          ...(item === "new" ? {} : { isActive }),
          password: password || undefined,
        }),
      });
      await client.invalidateQueries({ queryKey: ["admin-users"] });
      onClose();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : editing
            ? "Không thể cập nhật người dùng."
            : "Không thể tạo người dùng.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={Boolean(item)}
      title={editing ? "Cập nhật người dùng" : "Thêm người dùng"}
      onClose={onClose}
    >
      <form className="modal-form" onSubmit={save}>
        <FormField label="Email">
          <input
            required
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </FormField>
        <FormField label="Tên hiển thị">
          <input
            required
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </FormField>
        <div className="form-field">
          <span>Vai trò</span>
          <div className="checkbox-list">
            {availableRoles.filter((role) => role.isActive || roleIds.includes(role.id)).map((role) => (
              <label key={role.id}>
                <input
                  type="checkbox"
                  checked={roleIds.includes(role.id)}
                  disabled={!role.isActive}
                  onChange={() => toggleRole(role.id)}
                />{" "}
                {roleDisplayName(role)}{!role.isActive ? " (đã ngừng dùng)" : ""}
              </label>
            ))}
          </div>
        </div>
        {editing && (
          <FormField label="Trạng thái">
            <select
              value={isActive ? "active" : "inactive"}
              onChange={(event) => setIsActive(event.target.value === "active")}
            >
              <option value="active">Đang dùng</option>
              <option value="inactive">Đã khóa</option>
            </select>
          </FormField>
        )}
        <FormField label={editing ? "Mật khẩu mới (tùy chọn)" : "Mật khẩu tạm (tùy chọn)"}>
          <input
            type="password"
            minLength={8}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <small>
            {editing
              ? "Để trống nếu không cần đổi mật khẩu."
              : "Để trống để hệ thống tạo mật khẩu tạm và gửi qua email."}
          </small>
        </FormField>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose}>
            Hủy
          </Button>
          <Button type="submit" disabled={saving}>
            <UserCog size={16} /> {editing ? "Lưu thay đổi" : "Tạo người dùng"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RoleModal({
  item,
  permissions,
  onClose,
}: {
  item: RoleDirectory["roles"][number] | "new" | null;
  permissions: RoleDirectory["permissions"];
  onClose(): void;
}) {
  const client = useQueryClient();
  const editing = item && item !== "new" ? item : null;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissionIds, setPermissionIds] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setDescription(editing.description);
      setPermissionIds(editing.permissions.map((permission) => permission.id));
      setIsActive(editing.isActive);
    } else {
      setName("");
      setDescription("");
      setPermissionIds(permissions.some((permission) => permission.id === "bom.read") ? ["bom.read"] : []);
      setIsActive(true);
    }
    setError("");
  }, [editing, item, permissions]);

  function togglePermission(permissionId: string) {
    setPermissionIds((current) => current.includes(permissionId)
      ? current.filter((id) => id !== permissionId)
      : [...current, permissionId]);
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!item) return;
    if (!permissionIds.length) {
      setError("Hãy chọn ít nhất một quyền thao tác cho vai trò này.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api(item === "new" ? "/admin/roles" : `/admin/roles/${editing?.id}`, {
        method: item === "new" ? "POST" : "PUT",
        body: JSON.stringify({ name, description, permissionIds, isActive }),
      });
      await client.invalidateQueries({ queryKey: ["admin-roles"] });
      await client.invalidateQueries({ queryKey: ["admin-users"] });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể lưu vai trò.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={Boolean(item)} title={editing ? "Cập nhật vai trò" : "Thêm vai trò"} onClose={onClose}>
      <form className="modal-form role-modal-form" onSubmit={save}>
        <FormField label="Tên vai trò">
          <input required value={name} onChange={(event) => setName(event.target.value)} placeholder="Ví dụ: Điều phối sản xuất" />
        </FormField>
        <FormField label="Mô tả ngắn">
          <textarea required rows={2} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Nêu phạm vi công việc của vai trò này" />
        </FormField>
        <div className="form-field">
          <span>Quyền thao tác</span>
          <div className="permission-picker">
            {permissions.map((permission) => (
              <label key={permission.id}>
                <input type="checkbox" checked={permissionIds.includes(permission.id)} onChange={() => togglePermission(permission.id)} />
                <span><strong>{permission.description}</strong></span>
              </label>
            ))}
          </div>
        </div>
        {editing && (
          <FormField label="Trạng thái">
            <select value={isActive ? "active" : "inactive"} onChange={(event) => setIsActive(event.target.value === "active")}>
              <option value="active">Đang dùng</option>
              <option value="inactive">Ngừng dùng</option>
            </select>
            <small>Khi ngừng dùng, vai trò này sẽ không còn cấp quyền cho người được gán.</small>
          </FormField>
        )}
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Hủy</Button>
          <Button type="submit" disabled={saving}>{editing ? "Lưu thay đổi" : "Tạo vai trò"}</Button>
        </div>
      </form>
    </Modal>
  );
}

function CustomFieldModal({
  item,
  onClose,
}: {
  item: CustomField | "new" | null;
  onClose(): void;
}) {
  const client = useQueryClient();
  const editing = item && item !== "new" ? item : null;
  const [appliesTo, setAppliesTo] = useState<"bom_header" | "bom_line">(
    "bom_header",
  );
  const [fieldKey, setFieldKey] = useState("");
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState<CustomField["field_type"]>("text");
  const [options, setOptions] = useState("");
  const [required, setRequired] = useState(false);
  const [active, setActive] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) {
      setAppliesTo(editing.applies_to);
      setFieldKey(editing.field_key);
      setLabel(editing.label);
      setFieldType(editing.field_type);
      setOptions(editing.selectOptions.join(", "));
      setRequired(Boolean(editing.is_required));
      setActive(Boolean(editing.is_active));
    } else {
      setAppliesTo("bom_header");
      setFieldKey("");
      setLabel("");
      setFieldType("text");
      setOptions("");
      setRequired(false);
      setActive(true);
    }
    setError("");
  }, [editing, item]);
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!item) return;
    setSaving(true);
    setError("");
    try {
      const body = {
        appliesTo,
        fieldKey,
        label,
        fieldType,
        selectOptions: options
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
        isRequired: required,
        isActive: active,
        sortOrder: 0,
      };
      await api(
        item === "new" ? "/custom-fields" : `/custom-fields/${editing?.id}`,
        {
          method: item === "new" ? "POST" : "PATCH",
          body: JSON.stringify(body),
        },
      );
      await client.invalidateQueries({ queryKey: ["custom-fields"] });
      onClose();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Không thể lưu trường tùy biến.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open={Boolean(item)}
      title={
        item === "new" ? "Thêm trường tùy biến" : "Cập nhật trường tùy biến"
      }
      onClose={onClose}
    >
      <form className="modal-form" onSubmit={save}>
        <FormField label="Áp dụng cho">
          <select
            value={appliesTo}
            disabled={Boolean(editing)}
            onChange={(event) =>
              setAppliesTo(event.target.value as "bom_header" | "bom_line")
            }
          >
            <option value="bom_header">Thông tin chung của định mức</option>
            <option value="bom_line">Chi tiết từng vật tư</option>
          </select>
          <small>“Thông tin chung” xuất hiện khi tạo định mức; “Chi tiết từng vật tư” xuất hiện khi thêm hoặc sửa một vật tư trong định mức.</small>
        </FormField>
        <FormField label="Mã nhận diện">
          <input
            required
            disabled={Boolean(editing)}
            value={fieldKey}
            pattern="[a-z][a-z0-9_]{1,60}"
            onChange={(event) => setFieldKey(event.target.value)}
            placeholder="vi_du: quy_cach"
          />
          <small>Chữ thường, số và dấu gạch dưới.</small>
        </FormField>
        <FormField label="Nhãn hiển thị">
          <input
            required
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        </FormField>
        <FormField label="Kiểu dữ liệu">
          <select
            value={fieldType}
            onChange={(event) =>
              setFieldType(event.target.value as CustomField["field_type"])
            }
          >
            <option value="text">Văn bản</option>
            <option value="number">Số</option>
            <option value="date">Ngày</option>
            <option value="boolean">Đúng / Sai</option>
            <option value="select">Danh sách chọn</option>
          </select>
        </FormField>
        {fieldType === "select" && (
          <FormField label="Lựa chọn, phân cách dấu phẩy">
            <input
              required
              value={options}
              onChange={(event) => setOptions(event.target.value)}
              placeholder="Ví dụ: Loại A, Loại B"
            />
          </FormField>
        )}
        <label className="check-field">
          <input
            type="checkbox"
            checked={required}
            onChange={(event) => setRequired(event.target.checked)}
          />{" "}
          Bắt buộc nhập
        </label>
        <label className="check-field">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />{" "}
          Đang sử dụng
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose}>
            Hủy
          </Button>
          <Button type="submit" disabled={saving}>
            Lưu trường
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteUser({ item }: { item: UserItem }) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function remove() {
    setBusy(true);
    setError("");
    try {
      await api(`/admin/users/${item.id}`, { method: "DELETE" });
      await client.invalidateQueries({ queryKey: ["admin-users"] });
      setOpen(false);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Không thể xóa người dùng.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setError("");
          setOpen(true);
        }}
        title="Xóa người dùng"
        aria-label={`Xóa ${item.email}`}
      >
        <Trash2 size={16} />
      </button>
      <ConfirmDialog
        open={open}
        title={error ? "Không thể xóa người dùng" : "Xóa người dùng"}
        message={
          error ||
          `Bạn có chắc muốn xóa người dùng “${item.display_name || item.email}” không? Thao tác này không thể hoàn tác.`
        }
        confirmLabel="Xóa người dùng"
        pending={busy}
        onClose={() => setOpen(false)}
        onConfirm={() => void remove()}
      />
    </>
  );
}

function DeleteCustomField({ id, label }: { id: string; label: string }) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function remove() {
    setBusy(true);
    setError("");
    try {
      await api(`/custom-fields/${id}`, { method: "DELETE" });
      await client.invalidateQueries({ queryKey: ["custom-fields"] });
      setOpen(false);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Không thể xóa trường tùy biến.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setError("");
          setOpen(true);
        }}
        title="Xóa trường"
        aria-label={`Xóa ${label}`}
      >
        <Trash2 size={16} />
      </button>
      <ConfirmDialog
        open={open}
        title={error ? "Không thể xóa trường" : "Xóa trường tùy biến"}
        message={
          error ||
          `Bạn có chắc muốn xóa trường “${label}” không? Thao tác này không thể hoàn tác.`
        }
        confirmLabel="Xóa trường"
        pending={busy}
        onClose={() => setOpen(false)}
        onConfirm={() => void remove()}
      />
    </>
  );
}
